<script setup lang="ts">
import { ref, onMounted } from 'vue'
import type { Project } from '../types'
import VideoPlayer from './VideoPlayer.vue'

defineOptions({ name: 'ProjectCarousel' })

defineProps<{
  projects: Project[]
}>()

const currentIndex = ref(0)
const carouselRef = ref<HTMLElement | null>(null)
const showingCreditsForIndex = ref<Record<number, boolean>>({})

const toggleCredits = (index: number) => {
  showingCreditsForIndex.value[index] = !showingCreditsForIndex.value[index]
}

onMounted(() => {
  if (carouselRef.value) {
    carouselRef.value.addEventListener('scroll', () => {
      if (!carouselRef.value) return
      const scrollPosition = carouselRef.value.scrollLeft
      const itemWidth = carouselRef.value.offsetWidth * 0.9
      currentIndex.value = Math.round(scrollPosition / itemWidth)
    })
  }
})
</script>

<template>
  <div class="relative w-full">
    <!-- Carousel container -->
    <div class="relative">
      <!-- Scrollable content -->
      <div 
        ref="carouselRef"
        class="overflow-x-auto overflow-y-visible w-full hide-scrollbar py-4"
        style="scroll-behavior: smooth;"
      >
        <div class="flex">
          <div 
            v-for="(project, index) in projects" 
            :key="index"
            class="w-[90%] flex-shrink-0 px-3 relative"
          >
            <!-- Top decoration lines -->
            <div class="absolute left-3 -top-5 w-[1px] h-3 bg-white/30"></div>
            <div class="absolute right-3 -top-5 w-[1px] h-3 bg-white/30"></div>

            <!-- Bottom decoration lines -->
            <div class="absolute left-3 -bottom-5 w-[1px] h-3 bg-white/30"></div>
            <div class="absolute right-3 -bottom-5 w-[1px] h-3 bg-white/30"></div>

            <!-- Project Card -->
            <div class="bg-[#222222] rounded-[10px] overflow-hidden relative" style="height: 530px;">
              <Transition name="fade" mode="out-in">
                <!-- Main Content -->
                <div v-if="!showingCreditsForIndex[index]" key="main">
                  <!-- Title and Type first -->
                  <div class="px-4 pt-4 pb-3">
                    <h2 class="text-base leading-[19px] font-['HM_Studio_Grotesk'] font-normal mb-1 line-clamp-1">{{ project.title }}</h2>
                    <div class="text-base leading-[19px] text-[#808080] font-['HM_Studio_Grotesk'] font-light">{{ project.type }}</div>
                  </div>

                  <!-- Video -->
                  <div class="relative px-4">
                    <VideoPlayer 
                      :video-src="project.videoSrc"
                      :autoplay="true"
                      :start-muted="true"
                      :onLoaded="() => {}"
                    />
                  </div>

                  <!-- Description -->
                  <div class="px-4 pb-6">
                    <p class="text-base leading-[19px] text-white font-normal" style="display: -webkit-box; -webkit-line-clamp: 8; -webkit-box-orient: vertical; overflow: hidden;">{{ project.description }}</p>
                  </div>
                </div>

                <!-- Credits View -->
                <div v-else key="credits">
                  <!-- Credits content -->
                  <div class="px-4 pt-4">
                    <div class="space-y-1">
                      <template v-for="(value, key) in project.credits" :key="key">
                        <div class="text-base leading-[19px]">
                          <span class="font-normal">{{ key }}:</span>
                          <span class="opacity-35 font-normal ml-1">{{ value }}</span>
                        </div>
                      </template>
                    </div>
                  </div>
                </div>
              </Transition>

              <!-- Credits button (outside transition) -->
              <div class="absolute bottom-0 left-0 right-0 px-4 pb-6 bg-[#222222]">
                <button 
                  @click="toggleCredits(index)"
                  class="mt-6 px-2 py-1 border border-white/35 rounded-[8px] text-[11px] mono tracking-[0.01em] text-white/35 uppercase inline-flex items-center"
                >
                  Credits<span class="transition-all duration-150 overflow-hidden" :class="{ 'w-0': !showingCreditsForIndex[index], 'w-4 ml-2': showingCreditsForIndex[index] }">×</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Pagination dots -->
    <div class="flex justify-center space-x-4 mt-8">
      <button 
        v-for="(_, index) in projects" 
        :key="index"
        @click="currentIndex = index"
        class="w-1.5 h-1.5 rounded-full transition-colors"
        :class="index === currentIndex ? 'bg-white' : 'bg-white/30'"
      />
    </div>
  </div>
</template>

<style scoped>
.hide-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.hide-scrollbar::-webkit-scrollbar {
  display: none;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style> 